'use client';

import React, { useRef, useState, useSyncExternalStore } from 'react';
import Slick from 'react-slick';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import { useTranslations } from 'use-intl';
import ProductTileV2 from '@/components/molecules/product-tile-v2';
import Skeleton from '@/components/atoms/skeleton';
import useMediaQuery from '@/hooks/useMediaQuery';
import { classnames } from '@/utils/classnames/classnames';
import Slider from '../slider';
import { FeaturedProductsCarouselProps } from './types';

const SLIDES_TO_SHOW = { base: 1, sm: 2, lg: 4, xl: 5 };
const AUTOPLAY_INTERVAL_MS = 5000;

// react-slick takes an integer slidesToShow (the spec's 1.2 / 2.5 peek is an
// Embla-only feature; the project standardised on react-slick), so the visible
// count is integer per breakpoint.
const visibleAt = (width: number) => (width >= 1280 ? 5 : width >= 1024 ? 4 : width >= 640 ? 2 : 1);

// Hide skeleton cards beyond what's visible at each breakpoint (1 / 2 / 4 / 5).
const skeletonVisibility = (index: number) =>
  index >= 4 ? 'hidden xl:block' : index >= 2 ? 'hidden lg:block' : index >= 1 ? 'hidden sm:block' : '';

// EWM-760 — `prefers-reduced-motion` is a real external source (the OS/browser
// setting), so it's a textbook `useSyncExternalStore`: subscribe to the media
// query's 'change' event, read `.matches` as the client snapshot. A fresh
// `matchMedia` query is created per call (cheap) so subscribe/getSnapshot
// don't need to share a ref across renders.
const subscribeToReducedMotion = (callback: () => void) => {
  const query = window.matchMedia('(prefers-reduced-motion: reduce)');
  query.addEventListener('change', callback);
  return () => query.removeEventListener('change', callback);
};
const getReducedMotionSnapshot = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
// SSR/first-paint snapshot — matches the old `useState(false)` initializer.
const getReducedMotionServerSnapshot = () => false;

const usePrefersReducedMotion = () =>
  useSyncExternalStore(subscribeToReducedMotion, getReducedMotionSnapshot, getReducedMotionServerSnapshot);

/**
 * FeaturedProductsCarousel — "Trusted gear for every project" homepage section.
 * A react-slick carousel of the shared PLP `ProductTileV2` cards (role-aware
 * pricing + Add to Cart + Add to Purchase List), with header prev/next controls,
 * mobile swipe + dots, optional reduced-motion-safe autoplay, a loading
 * skeleton, and an a11y live region. Renders nothing when there are no products
 * (a "No products to display" placeholder shows only in Studio preview).
 */
const FeaturedProductsCarousel = ({
  eyebrow,
  heading,
  intro,
  role = 'master',
  canAddToPurchaseList,
  backOrdersAllowed = false,
  products,
  showActions = true,
  showRrp = true,
  autoplay = false,
  state = 'ready',
  isPreview = false,
  onAddToCart,
  onAddToPurchaseList,
  onProductClick,
}: FeaturedProductsCarouselProps) => {
  const translate = useTranslations();
  const sliderRef = useRef<Slick>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [width] = useMediaQuery();
  const reducedMotion = usePrefersReducedMotion();

  const isLoading = state === 'loading';
  const visible = visibleAt(width);
  const maxIndex = Math.max(0, products.length - visible);
  const canPrev = currentSlide > 0;
  const canNext = currentSlide < maxIndex;

  // Empty: render nothing on the live site; a placeholder only in Studio preview.
  if (!isLoading && products.length === 0) {
    if (!isPreview) return null;
    return (
      <section className="px-4 py-12 md:px-6 lg:px-12 lg:py-20">
        <div className="mx-auto max-w-[1200px] rounded-lg border border-dashed border-gray-300 p-10 text-center text-gray-500">
          {translate('common.no-products-to-display')}
        </div>
      </section>
    );
  }

  const Header = (
    <div className="mb-6 flex items-end justify-between gap-4 lg:mb-8">
      <div className="max-w-2xl">
        {eyebrow && (
          <span className="text-12 text-primary mb-2 flex items-center gap-2 font-semibold tracking-wide uppercase">
            <span aria-hidden className="bg-primary inline-block size-1.5 rounded-full" />
            {eyebrow}
          </span>
        )}
        <h2 className="text-22 lg:text-32 font-bold text-gray-800">{heading}</h2>
        {intro && <p className="text-14 lg:text-16 mt-3 leading-normal text-gray-600">{intro}</p>}
      </div>

      {!isLoading && (
        <div className="hidden shrink-0 gap-2 md:flex">
          {(['prev', 'next'] as const).map((dir) => {
            const disabled = dir === 'prev' ? !canPrev : !canNext;
            const Icon = dir === 'prev' ? ChevronLeftIcon : ChevronRightIcon;
            return (
              <button
                key={dir}
                type="button"
                aria-label={translate(dir === 'prev' ? 'common.previous' : 'common.next')}
                disabled={disabled}
                onClick={() => (dir === 'prev' ? sliderRef.current?.slickPrev() : sliderRef.current?.slickNext())}
                className={classnames(
                  'focus-visible:ring-primary flex size-10 items-center justify-center rounded-full border transition-colors focus-visible:ring-2 focus-visible:outline-none',
                  disabled
                    ? 'cursor-not-allowed border-gray-200 text-gray-300'
                    : 'hover:border-primary hover:text-primary border-gray-300 text-gray-700',
                )}
              >
                <Icon aria-hidden className="size-5" />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <section
      role="region"
      aria-roledescription="carousel"
      aria-label={heading}
      className="px-4 py-12 md:px-6 lg:px-12 lg:py-20"
    >
      <div className="mx-auto max-w-[1200px]">
        {Header}

        {isLoading ? (
          <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 lg:gap-6 xl:grid-cols-5">
            {Array.from({ length: 5 }).map((_, index) => (
              <li
                key={index}
                className={classnames('rounded-lg border border-gray-200 p-3', skeletonVisibility(index))}
              >
                <Skeleton className="block aspect-square w-full" />
                <Skeleton className="mt-3 block h-3 w-1/3" />
                <Skeleton className="mt-2 block h-4 w-3/4" />
                <Skeleton className="mt-3 block h-9 w-full" />
              </li>
            ))}
          </ul>
        ) : (
          <Slider
            ref={sliderRef}
            arrows={false}
            dots
            slidesToShow={SLIDES_TO_SHOW}
            spaceBetween={{ base: 12, md: 16, lg: 24 }}
            autoplay={autoplay && !reducedMotion}
            autoplaySpeed={AUTOPLAY_INTERVAL_MS}
            pauseOnHover
            pauseOnFocus
            afterChange={(index: number) => setCurrentSlide(index)}
            // Equal-height tiles (match the PLP grid): make the slick track a flex
            // row so slides stretch to the tallest, and pass height:100% down through
            // react-slick's two wrapper divs so the tile's `h-full` + bottom-anchored
            // CTA align across every card. Scoped to this carousel — not the shared
            // slider CSS — so other carousels are unaffected.
            // EWM-433 — force the track flush-left (`!mx-0`). slick-carousel's base
            // CSS sets `.slick-track { margin-inline: auto }`, which centres the track
            // when it's narrower than the list (fewer items than slidesToShow) — the
            // spare-parts/cart carousels with 1–3 spares rendered centred with big
            // side gaps. react-slick positions via `transform`, so the auto margins
            // only ever produce that underfill-centring; zeroing them left-aligns the
            // few-item case and is a no-op once the row fills or scrolls.
            containerClassName="[&_.slick-dots]:md:hidden [&_.slick-track]:!mx-0 [&_.slick-track]:!flex [&_.slick-slide]:!h-auto [&_.slick-slide>div]:!h-full [&_.slick-slide>div>div]:!h-full"
          >
            {products.map((entry, index) => {
              const sku = entry.item.sku ?? '';
              return (
                <ProductTileV2
                  key={sku}
                  item={entry.item}
                  brand={entry.brand}
                  role={role}
                  canAddToPurchaseList={canAddToPurchaseList}
                  backOrdersAllowed={backOrdersAllowed}
                  variant="grid"
                  showActions={showActions}
                  showRrp={showRrp}
                  priceUnavailable={entry.priceUnavailable}
                  stockUnavailable={entry.stockUnavailable}
                  onAddToCart={(qty) => onAddToCart(sku, qty)}
                  onAddToPurchaseList={() => onAddToPurchaseList(entry)}
                  onTileClick={() => onProductClick?.(sku, index + 1)}
                />
              );
            })}
          </Slider>
        )}

        {/* a11y: announce the active slide for screen readers on advance. */}
        <span aria-live="polite" className="sr-only">
          {!isLoading &&
            products.length > 0 &&
            translate('common.slide-x-of-y', { current: currentSlide + 1, total: products.length })}
        </span>
      </div>
    </section>
  );
};

export default FeaturedProductsCarousel;
